import React, { useState } from "react";
import { STORY_TYPES, STATUS_MAP, newStoryItem, downloadText } from "../../lib/gamecraft";
import { GlassCard, StatusDot, StatusSelect, TagInput } from "../gc/ui";
import { Plus, Trash, Sparkle } from "@phosphor-icons/react";
import { ai } from "../../lib/api";
import { toast } from "sonner";
import { useI18n } from "../../lib/i18n";
import ModuleShell, { EmptyCraft } from "../gc/ModuleShell";
import { studioHealth } from "../../lib/studioHealth";

export default function StoryPanel({ project, onChange, onJump }) {
  const { lang } = useI18n();
  const items = project.story_items || [];
  const [type, setType] = useState("chapter");
  const [selected, setSelected] = useState(items[0]?.id);
  const list = items.filter((i) => i.type === type);
  const current = items.find((i) => i.id === selected) || list[0];

  const setItems = (next) => onChange({ story_items: next });
  const update = (id, patch) => setItems(items.map((i) => (i.id === id ? { ...i, ...patch } : i)));

  const add = () => {
    const it = newStoryItem({ type, name: STORY_TYPES.find((t) => t.id === type)?.name + " nueva" });
    setItems([...items, it]);
    setSelected(it.id);
  };

  const suggest = async () => {
    try {
      const res = await ai.ideas({
        prompt: `Mejora o propone 3 entradas de ${type} para el juego 2D "${project.name}". Historia actual: ${project.gdd?.story || current?.body || "sin definir"}.`,
        category: "story",
        language: lang,
      });
      const lines = (res.text || "").split("\n").filter((l) => l.trim().length > 4).slice(0, 3);
      setItems([...items, ...lines.map((l) => newStoryItem({ type, name: l.slice(0, 42), body: l, status: "thinking", tags: ["ia"] }))]);
    } catch {
      toast.error("IA no disponible");
    }
  };

  const score = studioHealth(project).modules.find((m) => m.id === "story")?.score ?? 0;

  return (
    <ModuleShell
      title="Historia & Mundo"
      subtitle="Capítulos, misiones, secretos, facciones y lugares — con estados y etiquetas."
      beginner="5 beats: gancho, mundo, giro, clímax, coda. Una frase por beat basta."
      expert="Estados y tags alimentan el canvas. El objetivo de Proyecto se muestra en el playtest."
      score={score}
      nextLabel="Al motor"
      onNext={() => onJump?.("engine")}
      actions={(
        <>
          <button className="gc-btn gc-btn-ghost h-9 px-3" onClick={suggest}><Sparkle size={14} /> Mejorar con IA</button>
          <button
            className="gc-btn gc-btn-ghost h-9 px-3"
            onClick={() => {
              const beats = [
                { name: "Gancho", body: "Primera imagen y conflicto en 10 segundos." },
                { name: "Mundo", body: "Regla clara del lugar y lo que está en juego." },
                { name: "Giro", body: "Lo que el jugador creía se rompe." },
                { name: "Clímax", body: "El boss o el momento de no retorno." },
                { name: "Coda", body: "Qué queda después de ganar (o perder)." },
              ].map((b) => newStoryItem({ type: "chapter", name: b.name, body: b.body, status: "thinking", tags: ["beat"] }));
              setItems([...items, ...beats]);
              toast.success("5 beats de historia");
            }}
          >
            5 beats
          </button>
          <button className="gc-btn gc-btn-ghost h-9 px-3" onClick={() => downloadText(JSON.stringify(items, null, 2), "historia.json", "application/json")}>Exportar</button>
          <button className="gc-btn gc-btn-primary h-9 px-3" onClick={add}><Plus size={14} /> Añadir</button>
        </>
      )}
    >
    <div className="h-full flex flex-col gap-4 min-h-0">

      <div className="flex gap-2 overflow-x-auto">
        {STORY_TYPES.map((t) => {
          const n = items.filter((i) => i.type === t.id).length;
          return (
            <button
              key={t.id}
              onClick={() => { setType(t.id); setSelected(items.find((i) => i.type === t.id)?.id); }}
              className="px-3 py-2 rounded-2xl text-xs font-semibold border shrink-0"
              style={{
                borderColor: type === t.id ? t.color : "var(--gc-border)",
                background: type === t.id ? `${t.color}18` : "var(--gc-surface)",
              }}
            >
              {t.name} · {n}
            </button>
          );
        })}
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-0">
        <div className="lg:col-span-4 space-y-2 overflow-auto gf-scroll">
          <div className="relative pl-4">
            <div className="absolute left-1 top-2 bottom-2 w-0.5 rounded" style={{ background: "var(--gc-border)" }} />
            {list.map((it) => (
              <button
                key={it.id}
                onClick={() => setSelected(it.id)}
                className="w-full text-left gc-card p-3 mb-2 relative"
                style={{ boxShadow: selected === it.id ? "0 0 0 2px var(--gc-primary)" : undefined }}
              >
                <span className="absolute -left-[14px] top-4 w-3 h-3 rounded-full border-2 bg-white" style={{ borderColor: STATUS_MAP[it.status || "neutral"]?.color }} />
                <div className="flex items-center gap-2">
                  <StatusDot status={it.status} />
                  <span className="font-semibold text-sm truncate">{it.name}</span>
                </div>
                <p className="text-xs mt-1 line-clamp-2" style={{ color: "var(--gc-muted)" }}>{it.body || "Sin texto"}</p>
              </button>
            ))}
            {list.length === 0 && (
              <EmptyCraft
                title="Nada en este tipo"
                body="Un beat de 2 líneas vence a un lore dump. Empieza por el gancho."
                cta="Añadir entrada"
                onCta={add}
                tips={["Usa 5 beats para el acto I", "Etiqueta sidequest vs main", "El objetivo de Proyecto aparece en el HUD del playtest"]}
              />
            )}
          </div>
        </div>

        <div className="lg:col-span-8">
          {current ? (
            <GlassCard className="p-5 space-y-3 h-full">
              <div className="flex items-center justify-between gap-2">
                <input className="gc-input h-10 px-3 flex-1 font-semibold" value={current.name} onChange={(e) => update(current.id, { name: e.target.value })} />
                <button className="gc-btn gc-btn-ghost h-10 px-3 text-red-500" onClick={() => { setItems(items.filter((i) => i.id !== current.id)); setSelected(null); }}>
                  <Trash size={14} />
                </button>
              </div>
              <StatusSelect value={current.status || "neutral"} onChange={(s) => update(current.id, { status: s })} />
              <textarea className="gc-input w-full p-3 min-h-[220px] text-sm" value={current.body || ""} onChange={(e) => update(current.id, { body: e.target.value })} placeholder="Escribe la escena, misión, secreto…" />
              <TagInput tags={current.tags || []} onChange={(tags) => update(current.id, { tags })} />
              <input className="gc-input h-10 px-3 w-full text-sm" placeholder="Subcategoría (ej: Acto I, Sidequest…)" value={current.subcategory || ""} onChange={(e) => update(current.id, { subcategory: e.target.value })} />
            </GlassCard>
          ) : (
            <GlassCard className="h-full flex items-center justify-center text-sm" style={{ color: "var(--gc-muted)" }}>Selecciona o crea una entrada.</GlassCard>
          )}
        </div>
      </div>
    </div>
    </ModuleShell>
  );
}
